/**
 * Self-service monthly attendance for any role.
 * Shows the calendar grid + Present / Holidays / Weekly-Off / Leaves / Absent
 * summary + leave-type breakup for the SIGNED-IN user only.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { fmtDate } from "@/lib/dates";
import { MonthPicker } from "@/components/DatePicker";

function thisMonth() { return new Date().toISOString().slice(0, 7); }

const STATUS_CLASS = {
  "Present": "status-completed",
  "Working": "status-ongoing",
  "Holiday": "status-ongoing",
  "Weekly Off": "status-pending",
  "Leave": "status-rejected",
  "Half Day": "status-pending",
  "Short Day": "status-cancelled",
  "Absent": "status-cancelled",
  "—": "",
};
const STATUS_SHORT = {
  "Present": "P",
  "Working": "W",
  "Holiday": "H",
  "Weekly Off": "WO",
  "Leave": "L",
  "Half Day": "½",
  "Short Day": "S",
  "Absent": "A",
  "—": "·",
};

function fmtTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

export default function MyAttendance() {
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const [row, setRow] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/attendance/monthly-stats", { params: { month, user_id: user.user_id } });
        const first = (r.data?.rows || [])[0] || null;
        setRow(first);
        setHolidays(r.data?.holidays || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [month, user.user_id]);

  const s = row?.summary;

  return (
    <div className="min-h-screen bg-white" data-testid="my-attendance-page">
      <PageHeader title="My Attendance" subtitle="Monthly statement" />

      <div className="px-4 sm:px-8 py-6 space-y-5">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="gc-label mb-1">Month</div>
            <MonthPicker
              value={month}
              onChange={(v) => setMonth(v || month)}
              className="w-44"
              testId="input-my-month"
            />
          </div>
          <div className="ml-auto inline-flex flex-wrap items-center gap-3 text-[10px] gc-label">
            <Legend color="status-completed" label="Present (P)" />
            <Legend color="status-ongoing" label="Holiday (H)" />
            <Legend color="status-pending" label="Weekly Off (WO)" />
            <Legend color="status-rejected" label="Leave (L)" />
            <Legend color="status-cancelled" label="Absent (A)" />
          </div>
        </div>

        {loading && <div className="text-sm text-slate-500" data-testid="my-att-loading">Loading…</div>}

        {!loading && row && (
          <>
            <div className="border border-slate-200" data-testid="my-att-summary">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold" style={{ fontFamily: "Outfit" }}>{row.user_name}</div>
                  <div className="text-[11px] text-slate-500 font-mono-data">{row.user_email} · {row.role}</div>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-[11px]">
                  <Stat label="Present" value={s.present} testId="stat-present" />
                  <Stat label="Holidays" value={s.holidays} testId="stat-holidays" />
                  <Stat label="Weekly Off" value={s.weekly_off} testId="stat-weekly-off" />
                  <Stat label="Leaves" value={s.leaves_total} testId="stat-leaves" />
                  <Stat label="Absent" value={s.absent} accent="red" testId="stat-absent" />
                  <Stat label="Paid Days" value={s.paid_days} accent="primary" testId="stat-paid-days" />
                </div>
              </div>
              <div className="px-3 py-3">
                <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(42px, 1fr))" }}>
                  {row.days.map((d) => (
                    <div
                      key={d.date}
                      title={`${fmtDate(d.date)} (${d.weekday}) — ${d.status}${d.half_day_period ? ` (${d.half_day_period})` : ""}${d.check_in ? ` · in ${fmtTime(d.check_in)}` : ""}${d.check_out ? ` · out ${fmtTime(d.check_out)}` : ""}${d.worked_hours != null ? ` · ${d.worked_hours}h worked` : ""}${d.leave_type ? ` · ${d.leave_type}` : ""}${d.holiday_name ? ` · ${d.holiday_name}` : ""}`}
                      className={`status-pill ${STATUS_CLASS[d.status] || ""} text-[10px] flex flex-col items-center justify-center leading-tight`}
                      style={{ minWidth: 36, minHeight: 30, padding: "2px 0", textAlign: "center" }}
                      data-testid={`my-day-${d.date}`}
                    >
                      <span className="font-semibold">
                        {new Date(d.date).getDate()}{STATUS_SHORT[d.status] !== "·" ? ` ${STATUS_SHORT[d.status]}` : ""}
                      </span>
                      {d.worked_hours != null && d.worked_hours > 0 && (
                        <span className="text-[8px] opacity-70 font-mono-data" data-testid={`my-day-hours-${d.date}`}>
                          {d.worked_hours}h
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {Object.keys(s.leaves_by_type || {}).length > 0 && (
                  <div className="mt-3 text-[11px] text-slate-500" data-testid="my-att-leave-breakup">
                    <span className="gc-label mr-2">Leave breakup</span>
                    {Object.entries(s.leaves_by_type).map(([k, v]) => (
                      <span key={k} className="mr-3 font-mono-data">{k}: <span className="text-[#0a2540]">{v}</span></span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Holidays this month */}
            <div className="border border-slate-200" data-testid="my-att-holidays">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                Holidays this month
              </div>
              {holidays.length === 0 ? (
                <div className="px-4 py-5 text-xs text-slate-400 text-center">None</div>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {holidays.map((h) => (
                    <li key={h.id} className="px-4 py-2 text-xs flex items-center justify-between">
                      <span className="font-medium">{h.name}</span>
                      <span className="font-mono-data text-[#0a2540]">{fmtDate(h.date)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Recent check-ins with location */}
            <div className="border border-slate-200" data-testid="my-att-recent">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                Recent check-ins
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-slate-500">
                    <tr>
                      <th className="text-left px-4 py-2">Date</th>
                      <th className="text-left px-4 py-2">In</th>
                      <th className="text-left px-4 py-2">Out</th>
                      <th className="text-left px-4 py-2">Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(row.days || []).filter((d) => d.check_in).slice().reverse().slice(0, 10).map((d) => (
                      <tr key={d.date} className="border-t border-slate-100">
                        <td className="px-4 py-2 font-mono-data text-[11px]">{fmtDate(d.date)}</td>
                        <td className="px-4 py-2 font-mono-data text-[11px]">{fmtTime(d.check_in)}</td>
                        <td className="px-4 py-2 font-mono-data text-[11px]">{fmtTime(d.check_out)}</td>
                        <td className="px-4 py-2">
                          {d.check_in_address ? (
                            <a
                              href={`https://www.google.com/maps?q=${d.check_in_lat},${d.check_in_lng}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[#0a2540] hover:underline"
                            >
                              📍 {d.check_in_address}
                            </a>
                          ) : d.check_in_lat ? (
                            <a
                              href={`https://www.google.com/maps?q=${d.check_in_lat},${d.check_in_lng}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-[#0a2540] hover:underline font-mono-data text-[11px]"
                            >
                              📍 {d.check_in_lat.toFixed(4)}, {d.check_in_lng.toFixed(4)}
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {(row.days || []).filter((d) => d.check_in).length === 0 && (
                      <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">No check-ins this month</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {!loading && !row && (
          <div className="text-xs text-slate-400 text-center py-10 border border-dashed border-slate-200">No data</div>
        )}
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1 normal-case tracking-normal">
      <span className={`status-pill ${color}`} style={{ minWidth: 18, padding: "0 6px" }}>·</span>
      <span>{label}</span>
    </span>
  );
}

function Stat({ label, value, accent, testId }) {
  const color = accent === "red" ? "text-red-700" : accent === "primary" ? "text-[#0a2540]" : "text-slate-800";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid={testId}>
      <span className="text-slate-500 uppercase tracking-wider text-[10px]">{label}</span>
      <span className={`font-mono-data font-semibold ${color}`}>{value}</span>
    </div>
  );
}
